import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent, Analytics } from "firebase/analytics";

const firebaseConfig = {
    apiKey: "AIzaSyAUPnDhoLn7rGD8pYME4Zcn6VINul9efZE",
    authDomain: "string-wizard.firebaseapp.com",
    projectId: "string-wizard",
    storageBucket: "string-wizard.firebasestorage.app",
    messagingSenderId: "69171723657",
    appId: "1:69171723657:web:12c9ecc7bd33a237d3b558",
    measurementId: "G-MLWPYRLCCJ",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Event deduplication cache
const eventCache = new Map<string, number>();
const DEBOUNCE_TIME = 1000; // 1 second debounce

// Helper function to create a cache key
const createCacheKey = (eventName: string, params: Record<string, any>) => {
    return `${eventName}:${JSON.stringify(params)}`;
};

// Debounced event logging function
const logEventWithDebounce = (
    analytics: Analytics,
    eventName: string,
    params: Record<string, any>
) => {
    const now = Date.now();
    const cacheKey = createCacheKey(eventName, params);
    const lastLog = eventCache.get(cacheKey);

    if (!lastLog || now - lastLog >= DEBOUNCE_TIME) {
        logEvent(analytics, eventName, params);
        eventCache.set(cacheKey, now);
    }
};

// Analytics event logging functions
export const logInstrumentSelection = (
    instrumentName: string,
    previousInstrument: string
) => {
    logEventWithDebounce(analytics, "instrument_selected", {
        instrument_name: instrumentName,
        previous_instrument: previousInstrument,
    });
};

export const logTuningSelection = (
    instrumentName: string,
    tuningName: string,
    previousTuning: string
) => {
    logEventWithDebounce(analytics, "tuning_selected", {
        instrument_name: instrumentName,
        tuning_name: tuningName,
        previous_tuning: previousTuning,
    });
};

export const logScaleSelection = (
    key: string,
    scale: string,
    instrumentName: string
) => {
    logEventWithDebounce(analytics, "scale_selected", {
        key,
        scale,
        instrument_name: instrumentName,
    });
};

export const logChordSelection = (
    chord: string,
    key: string,
    scale: string,
    instrumentName: string
) => {
    logEventWithDebounce(analytics, "chord_selected", {
        chord,
        key,
        scale,
        instrument_name: instrumentName,
    });
};

export const logCapoUsage = (
    fret: number,
    instrumentName: string,
    tuningName: string
) => {
    logEventWithDebounce(analytics, "capo_used", {
        fret,
        instrument_name: instrumentName,
        tuning_name: tuningName,
    });
};

export const logCustomTuningCreated = (
    instrumentName: string,
    tuningName: string,
    notes: string[]
) => {
    logEventWithDebounce(analytics, "custom_tuning_created", {
        instrument_name: instrumentName,
        tuning_name: tuningName,
        notes: notes.join(","),
    });
};

export const logFretboardConfig = (config: {
    frets: number;
    isLeftHanded: boolean;
    rotation: number;
    showFretNumbers: boolean;
    instrumentName: string;
}) => {
    logEventWithDebounce(analytics, "fretboard_config_changed", config);
};

export const logNoteViewModeChange = (
    mode: "letters" | "scaleDegrees",
    instrumentName: string
) => {
    logEventWithDebounce(analytics, "note_view_mode_changed", {
        mode,
        instrument_name: instrumentName,
    });
};

export const logFeedbackRequest = (instrumentName: string) => {
    logEventWithDebounce(analytics, "feedback_requested", {
        instrument_name: instrumentName,
    });
};

export const logModalOpen = (
    modalType: "instrument" | "scale" | "chord" | "settings",
    instrumentName: string
) => {
    logEventWithDebounce(analytics, "modal_opened", {
        modal_type: modalType,
        instrument_name: instrumentName,
    });
};

export const logChordStepperUsage = (
    instrumentName: string,
    key: string,
    scale: string,
    selectedChord: string,
    stepNumber: number,
    totalSteps: number
) => {
    logEventWithDebounce(analytics, "chord_stepper_used", {
        instrument_name: instrumentName,
        key,
        scale,
        selected_chord: selectedChord,
        step_number: stepNumber,
        total_steps: totalSteps,
    });
};

// Session tracking with deduplication
let sessionStarted = false;

export const logSessionStart = () => {
    if (!sessionStarted) {
        logEventWithDebounce(analytics, "session_started", {
            timestamp: new Date().toISOString(),
            is_pwa: window.matchMedia("(display-mode: standalone)").matches,
        });
        sessionStarted = true;
    }
};

export const logSessionDuration = (
    durationSeconds: number,
    instrumentName: string
) => {
    if (sessionStarted) {
        logEventWithDebounce(analytics, "session_ended", {
            duration_seconds: durationSeconds,
            is_pwa: window.matchMedia("(display-mode: standalone)").matches,
            instrument_name: instrumentName,
        });
        sessionStarted = false;
    }
};

export default analytics;
